import React, { useEffect, useState, useRef } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import FormularioTarefasDoProjeto from '../Formulario/FormularioTarefasDoProjeto';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';
import { ehPrincipal, salvarPrincipal } from '../utils/responsavelPrincipal';
import PopupHorasReais, { salvarHorasReais } from '../components/PopupHorasReais';

// Helper: listens for MobileFooter generic + event
function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

// Shared API call with multiple local fallbacks
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];

  let lastError = null;
  for (const url of urls) {
    try {
      const config = { method, headers: { 'Content-Type': 'application/json' } };
      const token = getAuthToken();
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      if (data && (method === 'POST' || method === 'PUT')) config.body = JSON.stringify(data);
      const res = await fetch(url, config);
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const ct = res.headers.get('Content-Type') || '';
        if (ct.includes('application/json')) {
          try { return await res.json(); } catch { return { success: true, status: res.status }; }
        }
        return await res.text();
      } else {
        // Tenta capturar erro do servidor
        const errorText = await res.text().catch(() => `Status ${res.status}`);
        lastError = `${url} - ${errorText}`;
        console.error(`Erro na URL ${url}:`, errorText);
      }
    } catch (e) {
      lastError = `${url} - ${e.message}`;
      console.error(`Erro ao conectar com ${url}:`, e);
    }
  }
  throw new Error(`Nenhuma URL da API funcionou para ${method} ${endpoint}. Último erro: ${lastError}`);
};

// Chamada multipart para upload de arquivos
const apiCallMultipart = async (endpoint, method = 'POST', formData = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];
  let lastError = null;
  for (const url of urls) {
    try {
      const res = await fetch(url, { method, body: formData });
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const text = await res.text();
        try { return JSON.parse(text); } catch { return text; }
      }
      const errorText = await res.text().catch(() => `Status ${res.status}`);
      lastError = errorText;
    } catch (e) {
      lastError = e.message;
    }
  }
  throw new Error(lastError || 'Upload falhou');
};

// Try multiple endpoint name variants (keeps compatibility if backend renamed controller)
const apiCallWithAlternatives = async (paths, method = 'GET', data = null) => {
  let lastErr = null;
  for (const p of paths) {
    try {
      return await apiCall(p, method, data);
    } catch (e) {
      lastErr = e;
      console.warn(`apiCallWithAlternatives: tentativa falhou para ${p}:`, e.message || e);
    }
  }
  throw lastErr || new Error('Nenhuma rota alternativa funcionou: ' + paths.join(', '));
};

export default function TarefasProjetoDropdown() {
  const [tarefas, setTarefas] = useState([]);
  const [filtradas, setFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtro
  const [filtroCampo, setFiltroCampo] = useState('descricao');
  const [filtroValor, setFiltroValor] = useState('');
  const [dropdownAberto, setDropdownAberto] = useState(false);

  // Modal detalhe
  const [detalheAberto, setDetalheAberto] = useState(false);
  const [selecionada, setSelecionada] = useState(null);
  const [responsaveisTarefa, setResponsaveisTarefa] = useState([]);
  const [horasDetalhe, setHorasDetalhe] = useState([]);

  // Modal edição
  const [editarAberto, setEditarAberto] = useState(false);
  const [tarefaEditando, setTarefaEditando] = useState(null);
  // Status gravado da tarefa aberta para edição (para saber se ela está sendo concluída agora)
  const [statusOriginalEditando, setStatusOriginalEditando] = useState('');
  // true = aberta pelo "Ver Detalhes" (o mesmo formulário da edição, sem salvar)
  const [somenteLeituraEditando, setSomenteLeituraEditando] = useState(false);
  // Conclusão da tarefa: pop-up das horas reais dos responsáveis; o que foi informado fica aqui até salvar
  const [popupHorasReaisAberto, setPopupHorasReaisAberto] = useState(false);
  const horasReaisConclusaoRef = useRef(null);
  const [responsaveisTarefaEditando, setResponsaveisTarefaEditando] = useState([]);
  const [horasEditando, setHorasEditando] = useState([]);

  // Modal adicionar responsável na edição
  const [adicionarRespEditarAberto, setAdicionarRespEditarAberto] = useState(false);
  const [novoRespEdicao, setNovoRespEdicao] = useState({
    responsavelId: '',
    horasPrevistas: '',
    horasReais: ''
  });

  // Modal criar nova tarefa
  const [criarAberto, setCriarAberto] = useState(false);
  const [novaTarefa, setNovaTarefa] = useState({
    descricao: '',
    dataInicioPrevisto: '',
    dataInicioReal: '',
    dataFimPrevisto: '',
    dataFimReal: '',
    responsaveis: [''], // Array de IDs de pessoas
    status: 'Pendente',
    observacoes: '',
    producaoId: '',
    id_cliente: '',
    tipoTarefaId: null
  });

  // Array para armazenar horas previstas e reais de cada responsável
  const [horasResponsaveis, setHorasResponsaveis] = useState([
    { horasPrevistas: '', horasReais: '' }
  ]);

  // Modal de seleção de produção
  const [selecionarProducaoAberto, setSelecionarProducaoAberto] = useState(false);
  const [producoes, setProducoes] = useState([]);
  const [producoesFiltradas, setProducoesFiltradas] = useState([]);
  const [filtroProducao, setFiltroProducao] = useState('');
  const [tipoFiltroProducao, setTipoFiltroProducao] = useState('todos');
  const [loadingProducoes, setLoadingProducoes] = useState(false);

  // Lista de responsáveis
  const [responsaveis, setResponsaveis] = useState([]);
  const [loadingResponsaveis, setLoadingResponsaveis] = useState(false);

  // Dados completos dos responsáveis selecionados (para manter perfil, custoHora)
  const [responsaveisDados, setResponsaveisDados] = useState([]);

  // Armazenar horas totais por tarefa { tarefaId: { horasPrevistas: x, horasReais: y } }
  const [horasPorTarefa, setHorasPorTarefa] = useState({});

  // Modal de criar novo responsável
  const [criarResponsavelAberto, setCriarResponsavelAberto] = useState(false);
  const [novoResponsavel, setNovoResponsavel] = useState({
    nome: '',
    custoHora: ''
  });

  // Refs para FormularioMateriaPrima (para chamar alocarMateriaisParaTarefa)
  const refFormularioMateriaPrimaCriar = useRef(null);
  const refFormularioMateriaPrimaEditar = useRef(null);

  // Estados para upload de fotos na edição de tarefa
  const [selectedFilesTarefa, setSelectedFilesTarefa] = useState([]);

  // Lista de tipos de tarefa
  const [tiposTarefa, setTiposTarefa] = useState([]);

  useEffect(() => {
    fetchTarefas();
    fetchTiposTarefa();
  }, []);

  // Dispatch form popup state for AjudaWidget
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('tarefas-popup-state', {
      detail: { formularioAberto: editarAberto || criarAberto }
    }));
  }, [editarAberto, criarAberto]);

  // Listen for AjudaWidget close-all-popups event
  useEffect(() => {
    const handler = () => {
      setEditarAberto(false);
      setCriarAberto(false);
      setAdicionarRespEditarAberto(false);
      setSelecionarProducaoAberto(false);
      setCriarResponsavelAberto(false);
      setDetalheAberto(false);
      setDropdownAberto(false);
      setSelecionada(null);
      setTarefaEditando(null);
    };
    window.addEventListener('tarefas-fechar-todos-popups', handler);
    return () => window.removeEventListener('tarefas-fechar-todos-popups', handler);
  }, []);

  // A lista de pessoas para escolher responsável é SEMPRE a de todas as pessoas cadastradas (fetchResponsaveis).
  // Antes, ao escolher a produção, ela era trocada só pelas pessoas já ligadas à produção (às vezes só a última).

  const fetchTiposTarefa = async () => {
    try {
      const data = await apiCall('TipoTarefa');
      setTiposTarefa(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erro ao carregar tipos de tarefa:', e);
      setTiposTarefa([]);
    }
  };

  const fetchProducoes = async () => {
    try {
      setLoadingProducoes(true);
      const data = await apiCall('Producoes');
      const arr = Array.isArray(data) ? data : [];
      setProducoes(arr);
      setProducoesFiltradas(arr);
    } catch (e) {
      console.error('Erro ao carregar projetos:', e);
      setProducoes([]);
      setProducoesFiltradas([]);
    } finally {
      setLoadingProducoes(false);
    }
  };

  const fetchResponsaveis = async () => {
    try {
      setLoadingResponsaveis(true);
      const data = await apiCall('Pessoas');
      const arr = Array.isArray(data) ? data : [];
      // Mapeia nome a partir de dadosUsuario.nomeUsuario ou usuarioSistema.login
      const mapped = arr.map(p => ({
        id: p.id || p.Id,
        nome: (p.dadosUsuario && p.dadosUsuario.nomeUsuario) ||
              (p.usuarioSistema && p.usuarioSistema.login) ||
              p.nome || p.Nome ||
              `Pessoa #${p.id || p.Id}`,
        custoHora: p.custoHora || p.CustoHora || 0
      }));
      setResponsaveis(mapped);
    } catch (e) {
      console.error('Erro ao carregar pessoas:', e);
      setResponsaveis([]);
    } finally {
      setLoadingResponsaveis(false);
    }
  };

  useEffect(() => {
    if (!filtroProducao.trim()) {
      setProducoesFiltradas(producoes);
      return;
    }
    const val = filtroProducao.toLowerCase();
    const filtrado = producoes.filter(p => {
      const id = (p.producaoId || p.ProducaoId || '').toString().toLowerCase();
      const contratoId = (p.contratosId || p.ContratosId || '').toString().toLowerCase();
      const oportunidadeId = (p.oportunidadeId || p.OportunidadeId || '').toString().toLowerCase();
      const titulo = (p.oportunidadeTitulo || p.OportunidadeTitulo || '').toLowerCase();
      const clienteId = (p.clienteId || p.ClienteId || '').toString().toLowerCase();
      const vendaId = (p.vendaId || p.VendaId || '').toString().toLowerCase();

      if (tipoFiltroProducao === 'todos') {
        return id.includes(val) || contratoId.includes(val) || oportunidadeId.includes(val) || titulo.includes(val) || clienteId.includes(val) || vendaId.includes(val);
      } else if (tipoFiltroProducao === 'producaoId') {
        return id.includes(val);
      } else if (tipoFiltroProducao === 'contratoId') {
        return contratoId.includes(val);
      } else if (tipoFiltroProducao === 'oportunidadeId') {
        return oportunidadeId.includes(val);
      } else if (tipoFiltroProducao === 'titulo') {
        return titulo.includes(val);
      } else if (tipoFiltroProducao === 'clienteId') {
        return clienteId.includes(val);
      } else if (tipoFiltroProducao === 'vendaId') {
        return vendaId.includes(val);
      }
      return true;
    });
    setProducoesFiltradas(filtrado);
  }, [filtroProducao, tipoFiltroProducao, producoes]);

  const fetchTarefas = async () => {
    try {
      setLoading(true);
      const data = await apiCall('TarefasProjeto');
      const arr = Array.isArray(data) ? data : [];
      setTarefas(arr);
      setFiltradas(arr);
      setError('');
      
      // Buscar horas de cada tarefa
      await fetchTodasAsHoras(arr);
    } catch (e) {
      setError('Erro ao carregar tarefas. Verifique o backend.');
      setTarefas([]);
      setFiltradas([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodasAsHoras = async (tarefas) => {
    const horasMap = {};

    // Carregar todos os responsáveis uma única vez (será filtrado por tarefa)
    let todosResponsaveis = [];
    try {
      const respData = await apiCall('Responsaveis');
      todosResponsaveis = Array.isArray(respData) ? respData : [];
    } catch (e) {
      console.warn('Não foi possível carregar Responsaveis para computar horas por tarefa:', e);
      todosResponsaveis = [];
    }

    for (const tarefa of tarefas) {
      try {
        // filtrar responsáveis vinculados a esta tarefa (campo pode ter nomes diferentes)
        const vinculados = todosResponsaveis.filter(r => {
          const tid = r.tarefasId || r.TarefasId || r.tarefasID || r.TarefasID || r.TarefaId || r.tarefaId;
          return tid !== undefined && tid !== null && tid.toString() == tarefa.id.toString();
        });

        // As horas agora estão nas colunas de Responsaveis (HorasPrevistas/HorasReais), somamos direto
        const totalPrevistas = vinculados.reduce((s, r) => s + (parseFloat(r.horasPrevistas || r.HorasPrevistas || r.HorasPrevistas || 0) || 0), 0);
        const totalReais = vinculados.reduce((s, r) => s + (parseFloat(r.horasReais || r.HorasReais || 0) || 0), 0);

        horasMap[tarefa.id] = { horasPrevistas: totalPrevistas, horasReais: totalReais };
      } catch (e) {
        console.error(`Erro ao computar horas da tarefa ${tarefa.id}:`, e);
        horasMap[tarefa.id] = { horasPrevistas: 0, horasReais: 0 };
      }
    }
    setHorasPorTarefa(horasMap);
  };

  useEffect(() => {
    if (!filtroValor.trim()) { setFiltradas(tarefas); return; }
    const val = filtroValor.toLowerCase();
    const f = tarefas.filter(t => {
      if (filtroCampo === 'descricao') return (t.descricao || '').toLowerCase().includes(val);
      if (filtroCampo === 'status') return (t.status || '').toLowerCase().includes(val);
      if (filtroCampo === 'responsavel') return (t.responsavel || '').toLowerCase().includes(val);
      if (filtroCampo === 'producao') return (t.producaoId || '').toString().toLowerCase().includes(val);
      return true;
    });
    setFiltradas(f);
  }, [filtroValor, filtroCampo, tarefas]);

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('conclu')) return { bg: '#e6f4ea', text: '#1e8e3e' };
    if (s.includes('andamento')) return { bg: '#e3f2fd', text: '#1565c0' };
    if (s.includes('atrasad')) return { bg: '#fdecea', text: '#d93025' };
    if (s.includes('pendente')) return { bg: '#fff8e1', text: '#f9a825' };
    if (s.includes('cancelad')) return { bg: '#eceff1', text: '#455a64' };
    return { bg: '#f5f5f5', text: '#666' };
  };

  const fetchResponsaveisPorTarefa = async (tarefaId) => {
    try {
      // Buscar todos os responsáveis e também a tabela Pessoas para associar nome/custoHora
      const [respData, pessoasData] = await Promise.all([
        apiCall('Responsaveis'),
        apiCall('Pessoas')
      ]);
      const arr = Array.isArray(respData) ? respData : [];
      const pessoas = Array.isArray(pessoasData) ? pessoasData : [];

      // Filtra responsáveis que têm o tarefasId igual ao da tarefa selecionada
      const responsaveisVinculados = arr
        .filter(r => {
          const tid = r.tarefasId || r.TarefasId || r.tarefaId || r.TarefaId || r.TarefasID || r.tarefasID;
          return tid !== undefined && tid !== null && tid.toString() === tarefaId.toString();
        })
        .map(r => {
          const pessoaId = r.pessoasId || r.PessoasId || r.pessoaId || r.PessoaId;
          const pessoa = pessoas.find(p => (p.id || p.Id) == pessoaId);
          // Resolve nome: pessoa via dadosUsuario.nomeUsuario, ou PessoaNome do responsável
          const resolvedNome = pessoa
            ? ((pessoa.dadosUsuario && pessoa.dadosUsuario.nomeUsuario) ||
               (pessoa.usuarioSistema && pessoa.usuarioSistema.login) ||
               pessoa.nome || pessoa.Nome)
            : (r.PessoaNome || r.pessoaNome || r.nome || r.Nome);
          const resolvedCusto = pessoa
            ? (pessoa.custoHora || pessoa.CustoHora)
            : (r.PessoaCustoHora || r.pessoaCustoHora || r.custoHora || r.CustoHora);
          return {
            ...r,
            nome: resolvedNome || '—',
            custoHora: resolvedCusto,
            horasPrevistas: r.HorasPrevistas || r.horasPrevistas || 0,
            horasReais: r.HorasReais || r.horasReais || 0
          };
        });

      return responsaveisVinculados;
    } catch (e) {
      console.error('Erro ao carregar responsáveis da tarefa:', e);
      return [];
    }
  };

  // Buscar fotos da tarefa
  const fetchFotosPorTarefa = async (tarefaId) => {
    try {
      const data = await apiCall(`TarefaFoto/tarefa/${tarefaId}`);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      console.error('Erro ao buscar fotos da tarefa:', e);
      return [];
    }
  };

  // Envia as fotos escolhidas (ao criar ou editar a tarefa)
  const enviarFotosTarefa = async (tarefaId) => {
    if (!selectedFilesTarefa || selectedFilesTarefa.length === 0) return;
    console.log('📸 Enviando fotos para TarefaFoto...');
    for (const photoFile of selectedFilesTarefa) {
      try {
        const formData = new FormData();
        formData.append('TarefaId', tarefaId.toString());
        formData.append('Arquivo', photoFile.file);
        await apiCallMultipart('TarefaFoto', 'POST', formData);
        console.log(`✅ Foto enviada: ${photoFile.file.name}`);
      } catch (errFoto) {
        console.error('❌ Erro ao enviar foto:', errFoto);
        alert(`Não foi possível enviar a foto ${photoFile.file.name}: ${errFoto.message}`);
      }
    }
    selectedFilesTarefa.forEach(p => { try { URL.revokeObjectURL(p.preview); } catch {} });
    setSelectedFilesTarefa([]);
  };

  // Mensagem do servidor (ex.: projeto finalizado não aceita montagem) dentro do erro da chamada
  const mensagemDoErro = (error) => (String(error?.message || '').match(/"mensagem"\s*:\s*"([^"]+)"/) || [])[1];

  // Manipular seleção de arquivos (fotos novos)
  const handleFileChangeTarefa = (e) => {
    const files = Array.from(e.target.files || []);
    const images = files.filter(f => f.type && f.type.startsWith('image/'));
    if (!images.length) return;
    setSelectedFilesTarefa(prev => {
      const existingKeys = new Set(prev.map(p => `${p.file.name}_${p.file.size}`));
      const toAdd = [];
      for (const f of images) {
        const key = `${f.name}_${f.size}`;
        if (existingKeys.has(key)) continue;
        existingKeys.add(key);
        toAdd.push({ file: f, preview: URL.createObjectURL(f) });
      }
      return [...prev, ...toAdd];
    });
    e.target.value = null;
  };

  // Excluir foto da tarefa
  const handleExcluirFotoTarefa = async (fotoId) => {
    if (!fotoId) { alert('ID da foto não encontrado'); return; }
    try {
      setLoading(true);
      await apiCall(`TarefaFoto/${fotoId}`, 'DELETE');
      alert('Foto excluída com sucesso!');
      if (tarefaEditando && tarefaEditando.id) {
        const fotos = await fetchFotosPorTarefa(tarefaEditando.id);
        setTarefaEditando(prev => ({ ...prev, Fotos: fotos }));
      }
    } catch (e) {
      alert('Erro ao excluir foto: ' + e.message);
      console.error('Erro ao excluir foto:', e);
    } finally {
      setLoading(false);
    }
  };

  // "Ver Detalhes": o mesmo formulário da edição, com todos os dados, só para ver (sem salvar)
  const abrirDetalhe = (t) => abrirEditar(t, true);

  const tarefaConcluida = (t) => /conclu/i.test(t?.status || '');

  const abrirEditar = async (t, somenteLeitura = false) => {
    // Tarefa concluída não pode mais ser editada (mas os detalhes abrem)
    if (!somenteLeitura && tarefaConcluida(t)) {
      alert('Esta tarefa já foi concluída e não pode mais ser editada. Use "Ver Detalhes" para consultar.');
      return;
    }
    setSomenteLeituraEditando(somenteLeitura);
    horasReaisConclusaoRef.current = null;
    setStatusOriginalEditando(t.status || '');
    // Carregar fotos da tarefa antes de abrir
    const fotos = await fetchFotosPorTarefa(t.id);
    setTarefaEditando({ ...t, Fotos: fotos });
    setSelectedFilesTarefa([]);
    const resp = await fetchResponsaveisPorTarefa(t.id);
    setResponsaveisTarefaEditando(resp);
    
    // As horas agora estão nas colunas de Responsaveis (HorasPrevistas/HorasReais)
    try {
      const horasArr = resp.map(r => ({
        id: r.ResponsaveisId || r.id || r.ResponsaveisId || r.ResponsavelId || r.responsavelId,
        responsavelId: r.ResponsaveisId || r.id || r.ResponsaveisId || r.ResponsavelId || r.responsavelId,
        horasPrevistas: r.HorasPrevistas || r.horasPrevistas || 0,
        horasReais: r.HorasReais || r.horasReais || 0
      }));
      setHorasEditando(horasArr);
    } catch (e) {
      console.error('Erro ao montar horas para edição:', e);
      setHorasEditando([]);
    }
    
    await fetchResponsaveis(); // Carrega responsáveis disponíveis
    await fetchTiposTarefa(); // Carrega tipos de tarefa
    setEditarAberto(true);
  };

  const handleSalvarEdicao = async () => {
    if (somenteLeituraEditando) return; // "Ver Detalhes" não salva
    // Diminuição de matéria-prima (ou tarefa cancelada) sem dizer o destino do material: não salva
    if (refFormularioMateriaPrimaEditar.current?.validarAlteracoes && !refFormularioMateriaPrimaEditar.current.validarAlteracoes()) {
      return;
    }
    // Concluindo a tarefa: grava antes as matérias-primas (perdida → desperdiçada, sobra → estoque);
    // se der erro, a tarefa não é concluída (depois de concluída ela fica bloqueada)
    const concluindo = tarefaConcluida(tarefaEditando) && !/conclu/i.test(statusOriginalEditando || '');
    // Concluindo: as horas reais de cada responsável são informadas agora (no pop-up)
    if (concluindo && responsaveisTarefaEditando.length > 0 && horasReaisConclusaoRef.current === null) {
      setPopupHorasReaisAberto(true);
      return;
    }
    try {
      setLoading(true);
      if (concluindo && refFormularioMateriaPrimaEditar.current) {
        try {
          await refFormularioMateriaPrimaEditar.current.alocarMateriaisParaTarefa(tarefaEditando.id);
        } catch (errMat) {
          alert('A tarefa não foi concluída: erro nas matérias-primas: ' + (errMat.message || errMat));
          return;
        }
      }
      if (concluindo && horasReaisConclusaoRef.current) {
        try {
          await salvarHorasReais(horasReaisConclusaoRef.current);
        } catch (errHoras) {
          horasReaisConclusaoRef.current = null;
          alert('A tarefa não foi concluída: ' + (errHoras.message || errHoras));
          return;
        }
      }

      const resultadoPut = await apiCall(`TarefasProjeto/${tarefaEditando.id}`, 'PUT', tarefaEditando);
      // Montagem concluída sem entrega ligada: o servidor cria a entrega de montagem (agendada)
      const entregaMontagemId = resultadoPut?.entregaMontagemCriadaId;

      // POST de matérias-primas alocadas para a tarefa (edição)
      if (!concluindo && refFormularioMateriaPrimaEditar.current) {
        try {
          const resultAloc = await refFormularioMateriaPrimaEditar.current.alocarMateriaisParaTarefa(tarefaEditando.id);
          console.log('✅ Matérias-primas alocadas:', resultAloc);
        } catch (errMat) {
          console.error('Aviso: Erro ao alocar matérias-primas (tarefa foi atualizada):', errMat);
          alert('A tarefa foi salva, mas houve erro nas matérias-primas: ' + (errMat.message || errMat));
        }
      }

      // Upload de fotos (se houver novas)
      const hadPhotos = selectedFilesTarefa && selectedFilesTarefa.length > 0;
      await enviarFotosTarefa(tarefaEditando.id);

      alert((hadPhotos ? 'Tarefa atualizada e fotos enviadas com sucesso!' : 'Tarefa atualizada com sucesso!') +
        (entregaMontagemId ? `\n\nFoi criada a entrega de montagem #${entregaMontagemId} (agendada) em Entregas → Entregas de Serviços. Conclua-a lá, com a foto.` : ''));
      setEditarAberto(false);
      fetchTarefas();
    } catch (error) {
      alert(mensagemDoErro(error) || 'Erro ao atualizar tarefa');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdicionarResponsavelEdicao = async () => {
    if (!novoRespEdicao.responsavelId) {
      alert('Selecione uma pessoa');
      return;
    }


    try {
      // Buscar dados da pessoa selecionada
      const pessoaSelecionada = responsaveis.find(p => 
        (p.id || p.Id) == novoRespEdicao.responsavelId
      );

      if (pessoaSelecionada) {
        const producaoId = tarefaEditando.producaoId ? parseInt(tarefaEditando.producaoId) : null;
        
        const payloadResponsavel = {
          id: 0,
          tarefasId: parseInt(tarefaEditando.id),
          pessoasId: parseInt(novoRespEdicao.responsavelId),
          producaoId: producaoId,
          id_cliente: tarefaEditando.id_cliente ? parseInt(tarefaEditando.id_cliente) : null,
          // Ao adicionar, só horas previstas; as reais são lançadas ao concluir a tarefa
          horasPrevistas: parseFloat(novoRespEdicao.horasPrevistas) || 0
        };
        
        const respResult = await apiCall('Responsaveis', 'POST', payloadResponsavel);
        // As horas já foram gravadas no registro de Responsaveis (HorasPrevistas/HorasReais).

        alert('Responsável adicionado com sucesso!');
        setAdicionarRespEditarAberto(false);
        setNovoRespEdicao({ responsavelId: '', horasPrevistas: '', horasReais: '' });
        
        // Recarregar responsáveis e horas a partir dos registros de Responsaveis
        const resp = await fetchResponsaveisPorTarefa(tarefaEditando.id);
        setResponsaveisTarefaEditando(resp);
        const novasHorasEditando = resp.map(r => ({
          id: r.ResponsaveisId || r.id || r.ResponsaveisId || r.ResponsavelId || r.responsavelId,
          responsavelId: r.ResponsaveisId || r.id || r.ResponsaveisId || r.ResponsavelId || r.responsavelId,
          horasPrevistas: r.HorasPrevistas || r.horasPrevistas || 0,
          horasReais: r.HorasReais || r.horasReais || 0
        }));
        setHorasEditando(novasHorasEditando);
        await fetchResponsaveis();
      }
    } catch (error) {
      alert('Erro ao adicionar responsável: ' + error.message);
      console.error(error);
    }
  };

  // Marca/desmarca o responsável principal da tarefa (só um; com um responsável só, ele é o principal)
  const handleDefinirPrincipalEdicao = async (responsavel) => {
    try {
      const responsavelId = responsavel.id || responsavel.responsaveisId || responsavel.ResponsaveisId;
      await salvarPrincipal(apiCall, responsavelId, !ehPrincipal(responsavel));
      setResponsaveisTarefaEditando(await fetchResponsaveisPorTarefa(tarefaEditando.id));
    } catch (error) {
      alert('Erro ao definir o responsável principal: ' + error.message);
    }
  };

  const handleRemoverResponsavelEdicao = async (responsavel) => {
    if (!window.confirm(`Deseja realmente remover ${responsavel.nome || responsavel.Nome} desta tarefa?`)) {
      return;
    }

    try {
      const responsavelId = responsavel.id || responsavel.responsaveisId || responsavel.ResponsaveisId;
      
      // As horas agora estão no registro de Responsaveis (HorasPrevistas/HorasReais) e serão resetadas

      // 2. Atualizar o responsável para desvincular da tarefa (tarefasId = null ou 0)
      // Ou deletar o registro de responsável se for uma cópia específica da tarefa
      // Por enquanto, vamos apenas remover o vínculo
      const payloadResponsavel = {
        id: responsavelId,
        tarefasId: null,
        id_cliente: responsavel.id_cliente || 0,
        nome: responsavel.nome || responsavel.Nome,
        perfil: responsavel.perfil || responsavel.Perfil,
        custoHora: responsavel.custoHora || responsavel.CustoHora || 0
      , horasPrevistas: 0, horasReais: 0 };
      
      await apiCall(`Responsaveis/${responsavelId}`, 'PUT', payloadResponsavel);
      
      alert('Responsável removido com sucesso!');
      
      // Recarregar responsáveis e horas
      const resp = await fetchResponsaveisPorTarefa(tarefaEditando.id);
      setResponsaveisTarefaEditando(resp);
      // Recarregar horas: reconstruir via responsáveis vinculados
      try {
        const resp = await fetchResponsaveisPorTarefa(tarefaEditando.id);
        const horasArr = resp.map(r => ({
          id: r.ResponsaveisId || r.id || r.ResponsaveisId || r.ResponsavelId || r.responsavelId,
          responsavelId: r.ResponsaveisId || r.id || r.ResponsaveisId || r.ResponsavelId || r.responsavelId,
          horasPrevistas: r.HorasPrevistas || r.horasPrevistas || 0,
          horasReais: r.HorasReais || r.horasReais || 0
        }));
        setHorasEditando(horasArr);
      } catch (err) {
        console.warn('Erro ao recarregar horas após remoção:', err);
        setHorasEditando([]);
      }
      await fetchResponsaveis();
    } catch (error) {
      alert('Erro ao remover responsável: ' + error.message);
      console.error(error);
    }
  };

  const adicionarResponsavel = () => {
    setNovaTarefa({
      ...novaTarefa,
      responsaveis: [...novaTarefa.responsaveis, '']
    });
    setHorasResponsaveis([...horasResponsaveis, { horasPrevistas: '', horasReais: '' }]);
  };

  const removerResponsavel = (index) => {
    const novosResponsaveis = novaTarefa.responsaveis.filter((_, i) => i !== index);
    const novasHoras = horasResponsaveis.filter((_, i) => i !== index);
    setNovaTarefa({
      ...novaTarefa,
      responsaveis: novosResponsaveis
    });
    setHorasResponsaveis(novasHoras);
  };

  const atualizarResponsavel = (index, valor) => {
    const novosResponsaveis = [...novaTarefa.responsaveis];
    novosResponsaveis[index] = valor;
    setNovaTarefa({
      ...novaTarefa,
      responsaveis: novosResponsaveis
    });

    // Busca os dados completos do responsável selecionado (procura por ID ou PessoasId)
    const responsavelCompleto = responsaveis.find(r => 
      (r.id || r.Id || r.PessoasId || r.pessoasId) == valor
    );
    
    if (responsavelCompleto) {
      const novosDados = [...responsaveisDados];
      novosDados[index] = {
        nome: responsavelCompleto.nome || responsavelCompleto.Nome || responsavelCompleto.PessoaNome || responsavelCompleto.pessoaNome,
        perfil: responsavelCompleto.perfil || responsavelCompleto.Perfil,
        custoHora: responsavelCompleto.custoHora || responsavelCompleto.CustoHora || responsavelCompleto.PessoaCustoHora || responsavelCompleto.pessoaCustoHora
      };
      setResponsaveisDados(novosDados);
    }
  };

  const atualizarHorasResponsavel = (index, campo, valor) => {
    const novasHoras = [...horasResponsaveis];
    novasHoras[index] = {
      ...novasHoras[index],
      [campo]: valor
    };
    setHorasResponsaveis(novasHoras);
  };

  const handleCriarTarefa = async () => {
    try {
      setLoading(true);
      const pessoasEscolhidas = (novaTarefa.responsaveis || []).filter(Boolean).map(String);
      if (!novaTarefa.descricao || pessoasEscolhidas.length === 0) {
        alert('Preencha pelo menos Descrição e Responsável');
        setLoading(false);
        return;
      }
      if (new Set(pessoasEscolhidas).size !== pessoasEscolhidas.length) {
        alert('A mesma pessoa foi escolhida mais de uma vez como responsável.');
        setLoading(false);
        return;
      }

      // Horas previstas e reais são livres (um responsável não previsto no orçamento pode ter 0 previstas)

      // 1. POST da tarefa
      const tarefaCriada = await apiCall('TarefasProjeto', 'POST', novaTarefa);
      console.log('Tarefa criada:', tarefaCriada);

      // 2. POST de responsáveis vinculados à tarefa
      const tarefaId = tarefaCriada.id || tarefaCriada.Id || tarefaCriada.tarefaId || tarefaCriada.TarefaId;
      const producaoId = novaTarefa.producaoId ? parseInt(novaTarefa.producaoId) : null;
      
      if (tarefaId) {
        for (let i = 0; i < novaTarefa.responsaveis.length; i++) {
          const pessoaId = novaTarefa.responsaveis[i];
          if (pessoaId && pessoaId.toString().trim()) {
            try {
              // Busca os dados da pessoa
              // Vincula a pessoa escolhida (antes só vinculava se ela estivesse na lista carregada; senão era ignorada sem aviso)
              {
                const horas = horasResponsaveis[i] || { horasPrevistas: 0, horasReais: 0 };
                const payloadResponsavel = {
                  id: 0,
                  tarefasId: parseInt(tarefaId),
                  pessoasId: parseInt(pessoaId),
                  producaoId: producaoId,
                  id_cliente: novaTarefa.id_cliente ? parseInt(novaTarefa.id_cliente) : null,
                  // Na criação só horas previstas; as reais são lançadas ao concluir a tarefa
                  horasPrevistas: parseFloat(horas.horasPrevistas) || 0,
                  // Principal escolhido na criação (com um só, o servidor já marca como principal)
                  responsavelPrincipal: novaTarefa.responsavelPrincipalIndex === i
                };
                console.log('Criando responsável vinculado à tarefa:', payloadResponsavel);
                const respResult = await apiCall('Responsaveis', 'POST', payloadResponsavel);
                // As horas já são gravadas nas colunas de Responsaveis (HorasPrevistas/HorasReais)
              }
            } catch (errResp) {
              console.error('Erro ao vincular responsável:', errResp);
            }
          }
        }

        // 3. POST de matérias-primas alocadas para a tarefa
        if (refFormularioMateriaPrimaCriar.current) {
          try {
            const resultAloc = await refFormularioMateriaPrimaCriar.current.alocarMateriaisParaTarefa(tarefaId);
            console.log('✅ Matérias-primas alocadas:', resultAloc);
          } catch (errMat) {
            console.error('Aviso: Erro ao alocar matérias-primas (tarefa foi criada):', errMat);
            // Não bloqueia a criação da tarefa se matérias-primas falharem
          }
        }

        // 4. Fotos escolhidas no formulário
        await enviarFotosTarefa(tarefaId);
      }

      alert('Tarefa criada com sucesso!');
      setCriarAberto(false);
      setNovaTarefa({
        descricao: '',
        dataInicioPrevisto: '',
        dataInicioReal: '',
        dataFimPrevisto: '',
        dataFimReal: '',
        responsaveis: [''],
        status: 'Pendente',
        observacoes: '',
        producaoId: '',
        id_cliente: '',
        tipoTarefaId: null
      });
      setResponsaveisDados([]);
      setHorasResponsaveis([{ horasPrevistas: '', horasReais: '' }]);
      fetchTarefas();
    } catch (error) {
      alert(mensagemDoErro(error) || 'Erro ao criar tarefa');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const abrirFormularioResponsavel = () => {
    setNovoResponsavel({
      nome: '',
      custoHora: ''
    });
    setCriarResponsavelAberto(true);
  };

  const handleCriarResponsavel = async () => {
    try {
      if (!novoResponsavel.nome) {
        alert('O nome é obrigatório');
        return;
      }
      
      const payload = {
        id: 0,
        nome: novoResponsavel.nome,
        custoHora: novoResponsavel.custoHora ? parseFloat(novoResponsavel.custoHora) : 0
      };

      console.log('Payload sendo enviado:', payload);

      const pessoaCriada = await apiCall('Pessoas', 'POST', payload);
      alert('Pessoa criada com sucesso!');
      setCriarResponsavelAberto(false);
      await fetchResponsaveis(); // Recarrega lista de pessoas
      
      // Seleciona automaticamente a nova pessoa no primeiro campo
      const novosResponsaveis = [...novaTarefa.responsaveis];
      const pessoaId = pessoaCriada.id || pessoaCriada.Id;
      if (pessoaId) {
        novosResponsaveis[0] = pessoaId.toString();
        setNovaTarefa({ ...novaTarefa, responsaveis: novosResponsaveis });
      }
    } catch (error) {
      alert('Erro ao criar pessoa: ' + error.message);
      console.error('Erro completo:', error);
    }
  };

  const abrirSelecionarProducao = async () => {
    setSelecionarProducaoAberto(true);
    await fetchProducoes();
    await fetchResponsaveis();
    await fetchTiposTarefa();
  };

  const selecionarProducao = (producao) => {
    setNovaTarefa({
      ...novaTarefa,
      producaoId: producao.producaoId || producao.ProducaoId,
      id_cliente: producao.clienteId || producao.ClienteId || ''
    });
    setSelecionarProducaoAberto(false);
    setCriarAberto(true);
  };

  const Campo = ({ label, value }) => (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
      <label style={{ fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <span>{value ?? '—'}</span>
    </div>
  );

  return (
    <div className="container-geral fundo-branco" style={{ minHeight: 'calc(100vh - 60px)', background: '#f0f0f0', padding: 20 }}>
      <h2 style={{ textAlign: 'center', color: 'rgb(235, 133, 0)', marginBottom: 20 }}>✅ Tarefas do Projeto</h2>

      <MobileAddListener onOpen={abrirSelecionarProducao} />

      {/* Botão Adicionar Tarefa */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <button
          onClick={abrirSelecionarProducao}
          style={{
            padding: '12px 24px',
            backgroundColor: 'rgb(235, 133, 0)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 16,
            fontWeight: 600,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            transition: 'background 0.3s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(208, 116, 0)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(235, 133, 0)'}
        >
          + Adicionar Tarefa
        </button>
      </div>

      {/* Filtro */}
      <div className="filtro-container">
        <div className="filtro-wrapper">
          <input
            type="text"
            placeholder={`Filtrar por ${
              filtroCampo === 'descricao' ? 'Descrição' :
              filtroCampo === 'status' ? 'Status' :
              filtroCampo === 'responsavel' ? 'Responsável' :
              filtroCampo === 'producao' ? 'ID Projeto' : '—'
            }...`}
            value={filtroValor}
            onChange={(e) => setFiltroValor(e.target.value)}
            className="filtro-input"
          />
          <div className="dropdown-wrapper">
            <button className="dropdown-button" onClick={() => setDropdownAberto(!dropdownAberto)}>
              {filtroCampo === 'descricao' ? 'Descrição' :
               filtroCampo === 'status' ? 'Status' :
               filtroCampo === 'responsavel' ? 'Responsável' :
               filtroCampo === 'producao' ? 'ID Projeto' : 'Filtrar por'}
              <span className="dropdown-arrow">▼</span>
            </button>
            {dropdownAberto && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={() => { setFiltroCampo('descricao'); setDropdownAberto(false); }}>Descrição</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('status'); setDropdownAberto(false); }}>Status</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('responsavel'); setDropdownAberto(false); }}>Responsável</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('producao'); setDropdownAberto(false); }}>ID Projeto</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Estados */}
      {loading && (<div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Carregando tarefas...</div>)}
      {error && (
        <div style={{ textAlign: 'center', padding: 20, color: '#b00020', background: '#fdecea', borderRadius: 6, margin: '20px 0' }}>{error}</div>
      )}

      {/* Lista */}
      {!loading && !error && (
        <div className="tarefas-cards-container" style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 20 }}>
          {(!filtradas || filtradas.length === 0) ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666', background: 'white', marginTop: 10 }}>
              {filtroValor ? 'Nenhuma tarefa encontrada para o filtro' : 'Nenhuma tarefa cadastrada'}
            </div>
          ) : (
            filtradas.map((t, idx) => {
              const statusColors = getStatusColor(t.status);
              return (
                <div className="tarefa-card" key={t.id || idx} style={{
                  background: 'white',
                  borderRadius: 14,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                  border: `2.5px solid ${statusColors.bg}`,
                  padding: 32,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}>
                  <div className="tarefa-card-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                    <div className="tarefa-card-header" style={{ 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      marginBottom: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 12,
                        background: statusColors.bg,
                        color: statusColors.text,
                        fontWeight: 600,
                        fontSize: 13
                      }}>
                        {t.status || '—'}
                      </span>
                    </div>
                    
                    <div className="tarefa-card-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
                      <div className="tarefa-field tarefa-field-id">
                        <span className="tarefa-field-label" style={{ color: '#666', fontWeight: '600' }}>ID:</span>{' '}
                        <span className="tarefa-field-value">#{t.id}</span>
                      </div>

                      <div className="tarefa-field tarefa-field-descricao">
                        <span className="tarefa-field-label" style={{ color: '#666', fontWeight: '600' }}>Descrição:</span>{' '}
                        <span className="tarefa-field-value">{t.descricao}</span>
                      </div>

                      <div className="tarefa-field tarefa-field-producao">
                        <span className="tarefa-field-label" style={{ color: '#666', fontWeight: '600' }}>Projeto:</span>{' '}
                        <span className="tarefa-field-value">#{t.producaoId}</span>
                      </div>

                      <div className="tarefa-field tarefa-field-cliente">
                        <span className="tarefa-field-label" style={{ color: '#666', fontWeight: '600' }}>Cliente ID:</span>{' '}
                        <span className="tarefa-field-value">{t.id_cliente}</span>
                      </div>

                      <div className="tarefa-field tarefa-field-inicio">
                        <span className="tarefa-field-label" style={{ color: '#666', fontWeight: '600' }}>Início:</span>{' '}
                        <span className="tarefa-field-value">{t.dataInicioPrevisto ? new Date(t.dataInicioPrevisto).toLocaleDateString() : '—'}</span>
                      </div>

                      <div className="tarefa-field tarefa-field-fim">
                        <span className="tarefa-field-label" style={{ color: '#666', fontWeight: '600' }}>Fim:</span>{' '}
                        <span className="tarefa-field-value">{t.dataFimPrevisto ? new Date(t.dataFimPrevisto).toLocaleDateString() : '—'}</span>
                      </div>

                      <div className="tarefa-field tarefa-field-horas-reais">
                        <span className="tarefa-field-label" style={{ color: '#666', fontWeight: '600' }}>Hrs Reais:</span>{' '}
                        <span className="tarefa-field-value">{horasPorTarefa[t.id]?.horasReais?.toFixed(1) || '0'}</span>
                      </div>

                      {(t.tipoTarefaNome || t.tipoTarefaId) && (
                        <div className="tarefa-field tarefa-field-tipo">
                          <span className="tarefa-field-label" style={{ color: '#666', fontWeight: '600' }}>Tipo:</span>{' '}
                          <span className="tarefa-field-value" style={{ color: 'rgb(235, 133, 0)', fontWeight: 600 }}>
                            {t.tipoTarefaNome || tiposTarefa.find(tt => tt.id === t.tipoTarefaId)?.nome || `ID ${t.tipoTarefaId}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="tarefa-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
                    <button 
                      className="btn-detalhe-tarefa"
                      onClick={() => abrirDetalhe(t)}
                      style={{ 
                        background: 'rgb(235, 133, 0)', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 7, 
                        padding: '10px 24px', 
                        fontWeight: 600, 
                        fontSize: 16, 
                        minWidth: 120, 
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                    >
                      Ver Detalhes
                    </button>
                    <button
                      className="btn-editar-tarefa"
                      onClick={() => abrirEditar(t)}
                      disabled={tarefaConcluida(t)}
                      title={tarefaConcluida(t) ? 'Tarefa concluída: não pode mais ser editada' : 'Editar tarefa'}
                      style={{
                        background: tarefaConcluida(t) ? '#9e9e9e' : '#2196F3',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 7,
                        padding: '10px 24px',
                        fontWeight: 600,
                        fontSize: 16,
                        minWidth: 120,
                        cursor: tarefaConcluida(t) ? 'not-allowed' : 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                    >
                      {tarefaConcluida(t) ? '🔒 Concluída' : 'Editar'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Modal Detalhe */}
      {detalheAberto && selecionada && (
        <FormularioTarefasDoProjeto
          modo="detalhe"
          tarefa={selecionada}
          setTarefa={setSelecionada}
          responsaveisTarefa={responsaveisTarefa}
          setResponsaveisTarefa={setResponsaveisTarefa}
          horasTarefa={horasDetalhe}
          setHorasTarefa={setHorasDetalhe}
          responsaveis={responsaveis}
          apiCall={apiCall}
          apiCallWithAlternatives={apiCallWithAlternatives}
          fetchTarefas={fetchTarefas}
          tiposTarefa={tiposTarefa}
          onTiposTarefaRefresh={fetchTiposTarefa}
          onFechar={() => setDetalheAberto(false)}
        />
      )}

      {/* Modal Seleção de Projeto */}
      {selecionarProducaoAberto && (
        <div className="modal-overlay" onClick={() => setSelecionarProducaoAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '95%', maxWidth: 1000, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>📋 Selecione um Projeto</h3>
              <button onClick={() => setSelecionarProducaoAberto(false)} aria-label="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>

            {/* Filtro de Projeto */}
            <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="text"
                placeholder={`Filtrar por ${
                  tipoFiltroProducao === 'todos' ? 'todos os campos' :
                  tipoFiltroProducao === 'producaoId' ? 'ID Projeto' :
                  tipoFiltroProducao === 'contratoId' ? 'Contrato ID' :
                  tipoFiltroProducao === 'oportunidadeId' ? 'Oportunidade ID' :
                  tipoFiltroProducao === 'titulo' ? 'Título' :
                  tipoFiltroProducao === 'clienteId' ? 'Cliente ID' :
                  tipoFiltroProducao === 'vendaId' ? 'Venda ID' : ''
                }...`}
                value={filtroProducao}
                onChange={(e) => setFiltroProducao(e.target.value)}
                style={{ flex: 1, padding: '10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 }}
              />
              <select
                value={tipoFiltroProducao}
                onChange={(e) => setTipoFiltroProducao(e.target.value)}
                style={{ 
                  padding: '10px 16px', 
                  borderRadius: 6, 
                  border: '1px solid #ddd', 
                  fontSize: 14, 
                  backgroundColor: 'rgb(235, 133, 0)', 
                  color: 'white', 
                  cursor: 'pointer',
                  fontWeight: 600,
                  minWidth: 150
                }}
              >
                <option value="todos">Filtrar por: Todos</option>
                <option value="producaoId">ID Projeto</option>
                <option value="contratoId">Contrato ID</option>
                <option value="oportunidadeId">Oportunidade ID</option>
                <option value="titulo">Título</option>
                <option value="clienteId">Cliente ID</option>
                <option value="vendaId">Venda ID</option>
              </select>
            </div>

            {loadingProducoes ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Carregando projetos...</div>
            ) : producoesFiltradas.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Nenhum projeto encontrado</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                {producoesFiltradas.map((producao, idx) => {
                  const producaoId = producao.producaoId || producao.ProducaoId;
                  const contratoId = producao.contratosId || producao.ContratosId;
                  const oportunidadeId = producao.oportunidadeId || producao.OportunidadeId;
                  const titulo = producao.oportunidadeTitulo || producao.OportunidadeTitulo || 'Sem título';
                  const status = producao.status || producao.Status || 'N/A';
                  const clienteId = producao.clienteId || producao.ClienteId;
                  const vendaId = producao.vendaId || producao.VendaId;

                  return (
                    <div
                      key={producaoId || idx}
                      onClick={() => selecionarProducao(producao)}
                      style={{
                        padding: 16,
                        border: '2px solid #e0e0e0',
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: '#fafafa'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'rgb(235, 133, 0)';
                        e.currentTarget.style.backgroundColor = '#fff8f0';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.backgroundColor = '#fafafa';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'rgb(235, 133, 0)' }}>ID: {producaoId}</span>
                          <span style={{ 
                            fontSize: 11, 
                            padding: '2px 8px', 
                            borderRadius: 10, 
                            backgroundColor: status === 'Concluído' ? '#e6f4ea' : status === 'Em Andamento' ? '#e3f2fd' : '#fff8e1',
                            color: status === 'Concluído' ? '#1e8e3e' : status === 'Em Andamento' ? '#1565c0' : '#f9a825',
                            fontWeight: 600
                          }}>
                            {status}
                          </span>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#333', lineHeight: 1.3 }}>{titulo}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#666' }}>
                          <div><strong>Contrato ID:</strong> {contratoId || 'N/A'}</div>
                          <div><strong>Oportunidade ID:</strong> {oportunidadeId || 'N/A'}</div>
                          <div><strong>Cliente ID:</strong> {clienteId || 'N/A'}</div>
                          <div><strong>Venda ID:</strong> {vendaId || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conclusão da tarefa: horas reais de cada responsável */}
      {popupHorasReaisAberto && (
        <PopupHorasReais
          titulo="Concluir tarefa: horas reais dos responsáveis"
          responsaveis={responsaveisTarefaEditando.map(r => ({
            id: r.id || r.responsaveisId || r.ResponsaveisId,
            nome: r.nome || r.Nome,
            horasPrevistas: r.horasPrevistas ?? r.HorasPrevistas,
            horasReais: r.horasReais ?? r.HorasReais
          }))}
          onCancelar={() => setPopupHorasReaisAberto(false)}
          onConfirmar={(horas) => {
            horasReaisConclusaoRef.current = horas;
            setPopupHorasReaisAberto(false);
            setTimeout(() => handleSalvarEdicao(), 0);
          }}
        />
      )}

      {/* Modal Editar */}
      {editarAberto && tarefaEditando && (
        <FormularioTarefasDoProjeto
          modo={somenteLeituraEditando ? "detalhe" : "editar"}
          tarefa={tarefaEditando}
          setTarefa={setTarefaEditando}
          responsaveisTarefa={responsaveisTarefaEditando}
          setResponsaveisTarefa={setResponsaveisTarefaEditando}
          horasTarefa={horasEditando}
          setHorasTarefa={setHorasEditando}
          responsaveis={responsaveis}
          apiCall={apiCall}
          apiCallWithAlternatives={apiCallWithAlternatives}
          fetchTarefas={fetchTarefas}
          handleSalvar={handleSalvarEdicao}
          statusOriginal={statusOriginalEditando}
          handleRemoverResponsavel={handleRemoverResponsavelEdicao}
          onPessoaCriada={fetchResponsaveis}
          onDefinirPrincipal={handleDefinirPrincipalEdicao}
          adicionarRespAberto={adicionarRespEditarAberto}
          setAdicionarRespAberto={setAdicionarRespEditarAberto}
          novoResp={novoRespEdicao}
          setNovoResp={setNovoRespEdicao}
          handleAdicionarResponsavel={handleAdicionarResponsavelEdicao}
          refFormularioMateriaPrima={refFormularioMateriaPrimaEditar}
          selectedFiles={selectedFilesTarefa}
          setSelectedFiles={setSelectedFilesTarefa}
          handleFilesChange={handleFileChangeTarefa}
          onExcluirFoto={handleExcluirFotoTarefa}
          tiposTarefa={tiposTarefa}
          onTiposTarefaRefresh={fetchTiposTarefa}
          onFechar={() => {
            setEditarAberto(false);
            if (selectedFilesTarefa && selectedFilesTarefa.length > 0) {
              selectedFilesTarefa.forEach(p => { try { URL.revokeObjectURL(p.preview); } catch {} });
              setSelectedFilesTarefa([]);
            }
          }}
        />
      )}

      

      {/* Modal Criar Nova Tarefa */}
      {criarAberto && (
        <FormularioTarefasDoProjeto
          modo="criar"
          tarefa={novaTarefa}
          setTarefa={setNovaTarefa}
          responsaveisTarefa={[]}
          setResponsaveisTarefa={() => {}}
          horasTarefa={horasResponsaveis}
          setHorasTarefa={setHorasResponsaveis}
          responsaveis={responsaveis}
          apiCall={apiCall}
          apiCallWithAlternatives={apiCallWithAlternatives}
          fetchTarefas={fetchTarefas}
          handleSalvar={handleCriarTarefa}
          refFormularioMateriaPrima={refFormularioMateriaPrimaCriar}
          onPessoaCriada={fetchResponsaveis}
          selectedFiles={selectedFilesTarefa}
          setSelectedFiles={setSelectedFilesTarefa}
          handleFilesChange={handleFileChangeTarefa}
          tiposTarefa={tiposTarefa}
          onTiposTarefaRefresh={fetchTiposTarefa}
          onFechar={() => { setSelectedFilesTarefa([]); setCriarAberto(false); }}
        />
      )}

      {/* Modal Criar Novo Responsável */}
      {criarResponsavelAberto && (
        <div className="modal-overlay" onClick={() => setCriarResponsavelAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95060 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '90%', maxWidth: 600, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>👤 Criar Novo Responsável</h3>
              <button onClick={() => setCriarResponsavelAberto(false)} aria-label="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Nome *</label>
                <input
                  type="text"
                  value={novoResponsavel.nome}
                  onChange={(e) => setNovoResponsavel({ ...novoResponsavel, nome: e.target.value })}
                  style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                  placeholder="Nome da pessoa"
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Custo/Hora (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={novoResponsavel.custoHora}
                  onChange={(e) => setNovoResponsavel({ ...novoResponsavel, custoHora: e.target.value })}
                  style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={handleCriarResponsavel}
                style={{ padding: '12px 30px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Criar Responsável
              </button>
              <button
                onClick={() => setCriarResponsavelAberto(false)}
                style={{ padding: '12px 30px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
